import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps, useWindowDimensions } from 'react-native'
import {
  ApiError,
  DataWithUpload,
  Draft,
  FloorplanData,
  FloorplanWorkspaceData,
  useWorkspaceMutation,
  WorkspaceData,
  useFloorMutation,
  useFloorplanMutation,
  useStore,
} from 'zens-store'
import { Suspense, useState } from 'react'
import { useRouter, useNumberParams } from 'zens-router'
import { FloorplanEditor, FloorplanEditorStep, FloorplanHeader, LayoutHeader, NavigationDelete } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
})

interface Props extends ViewProps {}

const FloorEditScreen = (props: Props) => {
  const [confirmDelete, setConfirmDelete] = useState<Draft<FloorplanData>>()
  const [errors, setErrors] = useState<Record<string, string>>()
  const router = useRouter()
  const { message } = useStore()
  const { t } = useTranslation()
  const { style, ...otherProps } = props

  const [{ companyId, locationId, buildingId, floorId }] = useNumberParams()
  const floorplanMutation = useFloorplanMutation({ companyId })
  const floorMutation = useFloorMutation({ companyId })
  const workspaceMutation = useWorkspaceMutation({ companyId })
  const { height } = useWindowDimensions()
  const contentStyle = { minHeight: height }
  async function saveWorkspace(draft: Draft<WorkspaceData>) {
    try {
      setErrors({})
      if (!isNaN(draft.data.id)) {
        await workspaceMutation.update(draft)
        message.add({
          message: t('Workspace was updated successfully'),
          status: 'success',
        })
      } else {
        const { id } = await workspaceMutation.create(draft)
        draft.update('id', id)
        message.add({
          message: t('Workspace was created successfully'),
          status: 'success',
        })
      }
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`FLOORPLAN: Workspace save failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Workspace could not be saved')}. ${errorMessage}`,
          status: 'error',
        })
      }
    }
  }

  async function removeWorkspace(draft: Draft<WorkspaceData>) {
    try {
      setErrors({})
      await workspaceMutation.delete(draft)
      message.add({
        message: t('Workspace was removed successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`FLOORPLAN: Workspace remove failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Workspace could not be removed')}. ${errorMessage}`,
          status: 'error',
        })
      }
    }
  }

  async function duplicateWorkspaces(data: FloorplanWorkspaceData[], draft: Draft<FloorplanData>) {
    try {
      setErrors({})
      const newDraft = new Draft<FloorplanData>({
        ...draft.data,
        workspaces: data?.map((workspace) => {
          const { position: _, ...otherData } = workspace
          return otherData
        }),
      })
      const { workspaces } = await floorplanMutation.createWorkspaces(newDraft)
      message.add({
        message: t('Workspaces duplicated successfully'),
        status: 'success',
      })
      return workspaces?.map((workspace, index) => {
        return { ...workspace, position: data[index].position }
      })
    } catch (error) {
      const { message: errorMessage } = error as ApiError
      console.debug(`FLOORPLAN: Workspaces paste failed: ${errorMessage}`)
      message.add({
        message: `${t('Workspaces could not be duplicated')}. ${errorMessage}`,
        status: 'error',
      })
    }
  }

  async function save(draft: Draft<DataWithUpload<FloorplanData>>, shouldClose?: boolean) {
    try {
      setErrors({})
      const { upload, ...otherData } = draft.data
      if (upload || upload === null) {
        try {
          const newFloorplan = await floorplanMutation.upload(draft, upload)
          draft.update('floorplan', { ...draft.data.floorplan, background: newFloorplan.background })
        } catch (error) {
          const { message: errorMessage } = error as ApiError
          console.debug(`FLOORPLAN: Floorplan upload failed: ${errorMessage}`)
          message.add({
            message: `${t('Floor could not be saved')}. ${errorMessage}`,
            status: 'error',
          })
          draft.update('progress', undefined)
          return
        }
      }

      await floorplanMutation.update(new Draft(otherData))
      draft.settle()
      message.add({
        message: t('Floorplan was saved as draft successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`FLOORPLAN: Floorplan save failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Floor could not be saved')}. ${errorMessage}`,
          status: 'error',
        })
      }
      return
    }

    if (shouldClose) {
      router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`)
    }
  }

  async function remove() {
    try {
      await floorMutation.delete(confirmDelete!)
      message.add({
        message: t('Floor was deleted successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`FLOORPLAN: Floor deletion failed: ${errorMessage}`)
      message.add({
        message: `${t('Floor could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }
    router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`)
  }

  async function publish(draft: Draft<DataWithUpload<FloorplanData>>) {
    if (draft.hasChanges) {
      await save(draft)
    }
    try {
      await floorplanMutation.publish(draft)
    } catch (error) {
      const { message: errorMessage } = error as ApiError
      console.debug(`FLOORPLAN: Floorplan publish failed: ${errorMessage}`)
      message.add({
        message: `${t('Floorplan could not be published')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }

    message.add({
      message: t('Floorplan was published successfully'),
      status: 'success',
    })
    router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`)
  }

  async function discard(draft: Draft<DataWithUpload<FloorplanData>>) {
    try {
      await floorplanMutation.discard(draft)
    } catch (error) {
      const { message: errorMessage } = error as ApiError
      console.debug(`FLOORPLAN: Floorplan discard failed: ${errorMessage}`)
      message.add({
        message: `${t('Floorplan draft could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }
    message.add({
      message: t('Floorplan was discarded successfully'),
      status: 'success',
    })
    router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`)
  }

  async function cancel() {
    router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`)
  }

  function settings() {
    router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}/floors/${floorId}/settings`)
  }

  return (
    <View style={[styles.container, contentStyle, style]} {...otherProps}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <FloorplanHeader
          companyId={companyId}
          locationId={locationId}
          buildingId={buildingId}
          floorId={floorId}
          step={FloorplanEditorStep.Edit}
        />
      </Suspense>
      <Suspense fallback={<FloorplanEditor.Skeleton style={styles.content} step={FloorplanEditorStep.Edit} />}>
        <FloorplanEditor
          style={styles.content}
          companyId={companyId}
          buildingId={buildingId}
          floorId={floorId}
          step={FloorplanEditorStep.Edit}
          errors={errors}
          onSaveDraft={save}
          onSaveWorkspace={saveWorkspace}
          onRemoveWorkspace={removeWorkspace}
          onDuplicateWorkspaces={duplicateWorkspaces}
          onCancel={cancel}
          onDelete={async (draft) => setConfirmDelete(draft)}
          onPublish={publish}
          onDiscard={discard}
          onSettings={settings}
        />
      </Suspense>
      <NavigationDelete
        entity={t('Floor')}
        visible={!!confirmDelete}
        onClose={() => setConfirmDelete(undefined)}
        onDelete={remove}
      />
    </View>
  )
}

export default FloorEditScreen
